import { ChangeDetectionStrategy, Component, EventEmitter, inject, linkedSignal, Output } from "@angular/core";
import { AuthService, BillingService, OrganizationAccountGroupService } from "@front/m19-services";
import { combineLatest } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";
import { AccountMarketplace, AccountState } from "@front/m19-api-client";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  IBadgeComponent,
  IButtonDirective,
  ICheckboxComponent,
  IDataTableComponent,
  ITableDataSource,
  ITableImports,
  ITablePagination,
  ITableTextFilterConfig,
} from "@front/m19-ui";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountMarketplaceEx } from "@front/m19-models";

@Component({
  selector: "app-billing-account-selection",
  templateUrl: "./billing-account-selection.component.html",
  imports: [
    TranslocoDirective,
    IBadgeComponent,
    IButtonDirective,
    IDataTableComponent,
    ITableImports,
    ICheckboxComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingAccountSelectionComponent {
  private authService = inject(AuthService);
  private organizationService = inject(OrganizationAccountGroupService);
  private billingService = inject(BillingService);
  private readonly translocoService = inject(TranslocoService);

  private readonly DEFAULT_PAGE_SIZE = 15;

  @Output()
  save = new EventEmitter<AccountMarketplace[]>();
  @Output()
  cancelClicked = new EventEmitter();

  selectedOrganization$ = combineLatest([
    this.authService.user$,
    this.organizationService.allOrganizationAccountGroups$,
  ]).pipe(
    filter(([_, organizations]) => !!organizations && organizations.length > 0),
    map(([user, organizations]) => {
      return organizations!.find((x) => x.organization.ownerId == user!.userId)!;
    }),
  );

  profileIdsNotYetOn$ = this.selectedOrganization$.pipe(
    switchMap((selectedOrganization) => this.billingService.loadAvailableAccountMarketplaces(selectedOrganization.id)),
    map((r) => new Set(r.map((x) => x.profileId!))),
  );

  availableOrganizationResources$ = this.selectedOrganization$.pipe(
    map((selectedOrganization) => selectedOrganization?.accountGroups?.flatMap((ag) => ag.resources) ?? []),
  );

  alreadyBidderOn$ = this.availableOrganizationResources$.pipe(
    map((resources) => new Set(resources.filter((a) => a.state == AccountState.BIDDER_ON))),
  );
  alreadyBidderOn = toSignal(this.alreadyBidderOn$);

  alreadyUsedByAnotherOrganization$ = combineLatest([
    this.availableOrganizationResources$,
    this.profileIdsNotYetOn$,
    this.alreadyBidderOn$,
  ]).pipe(
    map(
      ([resources, profileIdsNotYetOn, alreadyBidderOn]) =>
        new Set(resources.filter((r) => !profileIdsNotYetOn.has(r.profileId!) && !alreadyBidderOn.has(r))),
    ),
  );
  alreadyUsedByAnotherOrganization = toSignal(this.alreadyUsedByAnotherOrganization$);

  selectableOrganizationResources$ = combineLatest([
    this.availableOrganizationResources$,
    this.alreadyUsedByAnotherOrganization$,
  ]).pipe(
    map(
      ([resources, alreadyUsedByAnotherOrganization]) =>
        new Set(
          resources.filter((o) => o.activated && !alreadyUsedByAnotherOrganization.has(o) && o.hasAccessToAdvertising),
        ),
    ),
  );
  selectableOrganizationResources = toSignal(this.selectableOrganizationResources$);

  selectedOrganizationResources = linkedSignal({
    source: this.alreadyBidderOn,
    computation: (alreadyBidderOn) => new Set(alreadyBidderOn),
    equal: () => false,
  });

  readonly textFilterConfig = new ITableTextFilterConfig<AccountMarketplaceEx>((o, filter) => {
    const toLowerCaseFilter = filter.toLowerCase();
    return (
      o.accountName.toLowerCase().includes(toLowerCaseFilter) || o.accountId.toLowerCase().includes(toLowerCaseFilter)
    );
  });
  readonly paginationConfig = new ITablePagination(this.DEFAULT_PAGE_SIZE);
  dataSource = new ITableDataSource(
    this.availableOrganizationResources$,
    undefined,
    this.paginationConfig,
    this.textFilterConfig,
  );

  readonly BidderOn = AccountState.BIDDER_ON;
  readonly activationMessage = this.translocoService.translate("billing-account-selection.unable_to_automate", {
    amazonAdvertisingConsole:
      '<a href="https://advertising.amazon.com/" target="_blank">' +
      this.translocoService.translate("common.amazon_advertising_console") +
      "</a>",
  });

  select(organizationResource: AccountMarketplace): void {
    const selectedOrganizationResources = this.selectedOrganizationResources();
    if (selectedOrganizationResources.has(organizationResource)) {
      selectedOrganizationResources.delete(organizationResource);
    } else {
      selectedOrganizationResources.clear();
      selectedOrganizationResources.add(organizationResource);
    }
    this.selectedOrganizationResources.set(selectedOrganizationResources);
  }

  isSelectable(organizationResource: AccountMarketplace): boolean {
    return !!(
      this.selectableOrganizationResources()?.has(organizationResource) &&
      !this.alreadyBidderOn()?.has(organizationResource)
    );
  }

  isSelected(organizationResource: AccountMarketplace): boolean {
    return this.selectedOrganizationResources().has(organizationResource);
  }

  saveSelection(): void {
    this.save.emit(Array.from(this.selectedOrganizationResources()));
  }

  cancelSelection(): void {
    this.cancelClicked.emit();
  }

  isValidSelection(): boolean {
    return this.selectedOrganizationResources().size > 0;
  }
}
